import { createElement, useState } from "react";
import { Trans } from "react-i18next";
import { useDotLegalSnackbar } from "@dotlegal/dotlegal-ui-components";
import { post, put } from "@common/api/apiShared";
import { Result } from "@common/api/result";
import { useValidator } from "@common/hooks/validation";
import { useDotLegalMutation } from "@common/hooks/dotLegalReactQuery";
import { useTranslation } from "@localization/useTranslation";
import { IAddEditIncidentTypeDialogProps } from "./AddEditIncidentTypeDialog";
import { IncidentTypeSaveModel } from "../incidentTypes/IncidentTypes.types";
import { incidentTypesUrl } from "../incidentTypes/IncidentTypes.hooks";

export function useAddEditIncidentTypeDialogHooks(props: IAddEditIncidentTypeDialogProps) {
    const { translateString } = useTranslation();
    const snackBar = useDotLegalSnackbar();
    const [saveModel, setSaveModel] = useState<IncidentTypeSaveModel>({ name: props.incidentTypeToUpdate?.name ?? "" });

    const validation = useValidator<IncidentTypeSaveModel>(
        (validators) => ({
            name: validators.validateNotEmpty((item) => item.name, translateString("name")),
        }),
        [saveModel]
    );
    validation.check(saveModel);

    const createMutation = useDotLegalMutation(createIncidentType, {
        onSuccess: (response: Result<unknown>) => onSaveResponse(response, "itemCreatedSuccess"),
    });

    const updateMutation = useDotLegalMutation(updateIncidentType, {
        onSuccess: (response: Result<unknown>) => onSaveResponse(response, "itemEditedSuccess"),
    });

    function createIncidentType() {
        return post<unknown>(incidentTypesUrl, saveModel);
    }

    function updateIncidentType(id: string) {
        return put<unknown>(`${incidentTypesUrl}/${id}`, saveModel);
    }

    function onSaveResponse(response: Result<unknown>, successTranslationKey: string) {
        if (response.success()) {
            snackBar.show(createElement(Trans, { i18nKey: successTranslationKey, values: { name: saveModel.name } }));
            props.onSaved();
        } else {
            snackBar.show(translateString("incidentTypeExists"), "warning");
        }
    }

    const onNameChange = (name: string) => {
        setSaveModel({ name });
    };

    const onSave = () => {
        if (validation.anyHasErrors) {
            validation.setShowErrors(true);
            return;
        }

        if (props.isAdd) {
            createMutation.mutate();
        } else {
            updateMutation.mutate(props.incidentTypeToUpdate!.id);
        }
    };

    return {
        isSaving: createMutation.isLoading || updateMutation.isLoading,
        validation,
        saveModel,
        onNameChange,
        onSave,
    };
}
